import React, { useContext, useEffect, useState } from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";
import { AlertTriangle, Loader2 } from "lucide-react";
import { hasUnsaved, unsavedEntries, setUnsaved, trackResumeSaves } from "@/lib/unsavedChanges";

// One warning for unsaved work, everywhere (2026-10-01). Covers:
//  - in-app navigation: links, NavLinks and navigate() all go through the
//    router's navigator, which is wrapped here (App.js uses a plain
//    <BrowserRouter>, so useBlocker isn't available);
//  - the browser/phone Back button: a capture-phase popstate listener,
//    installed at import time so it runs before the router's own;
//  - closing / reloading the tab: beforeunload (browser's own dialog).
const OPEN_EVENT = "rp360:unsaved-confirm";
let bypass = false; // one navigation allowed through after "Leave"

trackResumeSaves();

if (typeof window !== "undefined" && !window.__rp360PopGuard) {
  window.__rp360PopGuard = true;
  let restoring = false;
  window.addEventListener("popstate", (e) => {
    if (restoring) { restoring = false; e.stopImmediatePropagation(); return; }
    if (bypass || !hasUnsaved()) return;
    // Undo the Back/Forward step before the router sees it, then ask.
    e.stopImmediatePropagation();
    restoring = true;
    window.history.go(1);
    window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { proceed: () => { bypass = true; window.history.go(-1); setTimeout(() => { bypass = false; }, 500); } } }));
  }, true);
  window.addEventListener("beforeunload", (e) => {
    if (bypass || !hasUnsaved()) return;
    e.preventDefault();
    e.returnValue = "";
  });
}

export function UnsavedChangesGuard({ isLight }) {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const [pending, setPending] = useState(null); // { proceed }
  const [saving, setSaving] = useState(false);

  // Wrap push/replace so every in-app navigation can be held for a confirm.
  useEffect(() => {
    if (!navigator || navigator.__rp360Guarded) return undefined;
    const push = navigator.push.bind(navigator);
    const replace = navigator.replace.bind(navigator);
    const guard = (fn) => (...args) => {
      if (bypass || !hasUnsaved()) return fn(...args);
      setPending({ proceed: () => { bypass = true; fn(...args); setTimeout(() => { bypass = false; }, 0); } });
      return undefined;
    };
    navigator.push = guard(push);
    navigator.replace = guard(replace);
    navigator.__rp360Guarded = true;
    return () => { navigator.push = push; navigator.replace = replace; navigator.__rp360Guarded = false; };
  }, [navigator]);

  useEffect(() => {
    const onOpen = (e) => setPending(e.detail);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  if (!pending) return null;
  // The editor's own entry already covers a failed/in-flight resume save.
  const raw = unsavedEntries();
  const items = raw.some((i) => i.key === "resume-editor") ? raw.filter((i) => i.key !== "resume-save") : raw;
  const savers = items.filter((i) => i.save);
  const stay = () => setPending(null);
  const leave = () => { const p = pending; setPending(null); p.proceed(); };
  const saveAndLeave = async () => {
    setSaving(true);
    try {
      const results = await Promise.all(savers.map((i) => Promise.resolve(i.save()).catch(() => false)));
      if (results.every(Boolean)) { savers.forEach((i) => setUnsaved(i.key, false)); leave(); }
    } finally {
      setSaving(false);
    }
  };
  const messages = [...new Set(items.map((i) => i.message).filter(Boolean))];
  return (
    <div className={`fixed inset-0 z-[96] flex items-end sm:items-center justify-center backdrop-blur-md ${isLight ? "bg-[#FBF6EF]/60" : "bg-[#07140F]/60"}`} data-testid="unsaved-changes-modal">
      <div className={`w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl border p-6 shadow-2xl ${isLight ? "bg-white border-slate-200" : "bg-[#0F211B] border-white/10"}`}>
        <span className="grid size-11 place-items-center rounded-2xl bg-amber-500/15 text-amber-500"><AlertTriangle className="size-5" /></span>
        <h2 className={`mt-3 text-lg font-black ${isLight ? "text-slate-900" : "text-white"}`}>You have unsaved changes</h2>
        <ul className={`mt-1 space-y-1 text-sm ${isLight ? "text-slate-600" : "text-[#A9C2B6]"}`}>
          {(messages.length ? messages : ["Some of your changes aren't saved yet."]).map((m) => <li key={m}>{m}</li>)}
        </ul>
        <div className="mt-5 flex flex-col gap-2">
          {savers.length > 0 && (
            <button type="button" data-testid="unsaved-save-leave" onClick={saveAndLeave} disabled={saving} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#A67C52] via-[#C9A27A] to-[#A67C52] py-2.5 text-sm font-bold text-white disabled:opacity-60">
              {saving && <Loader2 className="size-4 animate-spin" />} Save and leave
            </button>
          )}
          <button type="button" data-testid="unsaved-stay" onClick={stay} className={`w-full rounded-full border py-2.5 text-sm font-bold ${isLight ? "border-slate-200 text-slate-800" : "border-white/15 text-white"}`}>
            Stay on this page
          </button>
          <button type="button" data-testid="unsaved-leave" onClick={leave} className="w-full py-2 text-xs font-bold text-rose-500 hover:underline">
            Leave without saving
          </button>
        </div>
      </div>
    </div>
  );
}
